(() => {
  if (window.__avryloCheckoutVariantSyncBound) return;
  window.__avryloCheckoutVariantSyncBound = true;

  const syncVersion = new WeakMap();
  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const getCheckout = (form) =>
    form?.querySelector('[ref="acceleratedCheckoutButtonContainer"], .accelerated-checkout-block');

  const setBusy = (form, busy) => {
    const checkout = getCheckout(form);
    if (!checkout) return;

    if (busy) {
      checkout.setAttribute('aria-busy', 'true');
      checkout.setAttribute('inert', '');
    } else {
      checkout.removeAttribute('aria-busy');
      checkout.removeAttribute('inert');
    }
  };

  const getMainProductForm = (picker) => {
    const scope = picker.closest('product-information') || picker.closest('[data-testid="product-information"]') || document;
    const productId = picker.dataset.productId;

    if (productId && window.CSS?.escape) {
      const exact = scope.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
      if (exact) return exact;
    }

    return scope.querySelector('product-form-component[data-product-id], product-form-component');
  };

  const waitForNativeVariantResolution = async (form, initialVariantId, token) => {
    const startedAt = performance.now();

    while (performance.now() - startedAt < 5000) {
      if (syncVersion.get(form) !== token) return null;

      const variantInput = form.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
      const currentVariantId = variantInput?.value?.trim() || '';

      if (currentVariantId !== initialVariantId) {
        return currentVariantId;
      }

      await sleep(40);
    }

    return null;
  };

  const refreshAcceleratedCheckout = async (form, variantId, token) => {
    const sectionId = form.dataset.sectionId;
    const productUrl = form.dataset.productUrl || window.location.pathname;
    const url = new URL(productUrl, window.location.origin);

    url.searchParams.delete('option_values');
    url.searchParams.set('variant', variantId);
    if (sectionId) url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`Checkout refresh failed: ${response.status}`);

    const markup = await response.text();
    if (syncVersion.get(form) !== token) return;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    let freshForm = null;

    if (sectionId && window.CSS?.escape) {
      freshForm = doc.querySelector(`product-form-component[data-section-id="${CSS.escape(sectionId)}"]`);
    }
    freshForm ||= doc.querySelector('product-form-component');

    const freshCheckout = freshForm?.querySelector(
      '[ref="acceleratedCheckoutButtonContainer"], .accelerated-checkout-block'
    );
    const currentCheckout = getCheckout(form);

    if (!currentCheckout) return;

    if (!freshCheckout) {
      currentCheckout.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();
    for (const node of freshCheckout.childNodes) {
      fragment.appendChild(node.cloneNode(true));
    }

    currentCheckout.replaceChildren(fragment);
    currentCheckout.toggleAttribute('hidden', freshCheckout.hasAttribute('hidden'));
    currentCheckout.dataset.avryloVariantId = variantId;

    if (window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const picker = target.closest('variant-picker');
      if (!picker) return;

      const form = getMainProductForm(picker);
      if (!form) return;

      const variantInput = form.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
      const initialVariantId = variantInput?.value?.trim() || '';
      const token = (syncVersion.get(form) || 0) + 1;
      syncVersion.set(form, token);
      setBusy(form, true);

      (async () => {
        let resolvedVariantId = null;

        try {
          resolvedVariantId = await waitForNativeVariantResolution(form, initialVariantId, token);
          if (syncVersion.get(form) !== token) return;

          if (!resolvedVariantId) {
            const currentId = form.querySelector('input[name="id"][ref="variantId"], input[name="id"]')?.value?.trim() || '';

            if (currentId === '') {
              const checkout = getCheckout(form);
              if (checkout) checkout.hidden = true;
            }
            return;
          }

          await refreshAcceleratedCheckout(form, resolvedVariantId, token);
        } catch (error) {
          if (syncVersion.get(form) === token) {
            const checkout = getCheckout(form);
            if (checkout) checkout.hidden = true;
          }
          console.warn('[AVRYLO] Accelerated checkout variant sync failed.', error);
        } finally {
          if (syncVersion.get(form) === token) {
            setBusy(form, false);
          }
        }
      })();
    },
    true
  );
})();
